/**
 * ErrorBoundary — catches unhandled render errors in a React subtree.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomePageComponent />
 *   </ErrorBoundary>
 *
 * On error, renders a friendly fallback card instead of a blank white screen.
 * The user can click "Try again" to reset the boundary and attempt a re-render.
 *
 * Error boundaries must be class components (React does not support
 * getDerivedStateFromError / componentDidCatch in functional components as of React 18).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { attemptChunkReload, isChunkLoadError } from '@utils/chunkReload';

// ---------------------------------------------------------------------------
// Props / State
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
  /** Optional custom fallback — receives a reset function. */
  fallback?: (reset: () => void) => ReactNode;
  /**
   * When this value changes, the boundary auto-resets. Pass `useLocation()
   * .pathname` (or any nav identifier) to make sure a single page's error
   * doesn't poison every subsequent route until manual refresh.
   */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production this is where you'd ship to Sentry / Datadog etc.
    console.error('[ErrorBoundary] Unhandled render error:', error, info.componentStack);

    // Stale-chunk recovery: this boundary wraps every workspace's routed
    // <Outlet/> (RootLayout, AdminLayout, StudentLayout, etc.), so it's the
    // one that actually catches a failed lazy() import after a redeploy
    // changes the chunk hashes — not the App-level boundary in
    // custom-components/error-boundary. Reload once (loop-guarded, see
    // utils/chunkReload.ts) to pick up the fresh index.html + hashes. If the
    // guard blocks it (another chunk-reload already happened in the last
    // ~10s), fall through to the normal fallback UI below unchanged — a
    // truly-broken chunk shouldn't reload forever.
    if (isChunkLoadError(error)) {
      attemptChunkReload();
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Auto-reset when the caller-supplied key changes (typically the route
    // pathname). Without this, a single page's render crash leaves the
    // boundary stuck in error state across every subsequent navigation —
    // which is exactly what poisoned all Super Admin pages today after
    // Organizations dialog threw.
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(this.reset);
    }

    // Default fallback UI
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={320}
        p={4}
      >
        <Stack alignItems="center" spacing={2} maxWidth={440} textAlign="center">
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />
          <Typography variant="h6" fontWeight={700}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </Typography>
          <Button variant="outlined" onClick={this.reset}>
            Try again
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
